/**
 * Every driver, and what they owe.
 *
 * The table the office works down at the end of a month. It opens sorted by what is
 * outstanding, descending, because that is the reason somebody opened it — and the total
 * row is the sum of what is displayed rather than of what was fetched, so a filtered table
 * adds up to what is on the screen.
 *
 * The four tiles above it are the other way round on purpose: they are the sum of the whole
 * fleet, and typing a name into the search box does not move them. The search is a way of
 * finding a driver in a long table, not a way of asking a second question about the period,
 * and a headline that changed while somebody typed would be a figure nobody could quote.
 * The total row says "of {{count}} shown" for exactly that reason.
 *
 * The takings columns sit beside the money owed on purpose. It is the only arrangement that
 * shows whether the daily rate is set anywhere near right, and the only place a firm would
 * notice a driver running work off the books. The two attributions disagree deliberately: a
 * ride belongs to the day it *finished* and a duty day to the day the shift *started*, so a
 * night shift lands in two different places. The screen says so, in the footnotes.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  cx,
  EmptyState,
  Icon,
  Input,
  StatTile,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { reportsApi, type FleetShiftBook, type FleetShiftBookRow } from './api';

type Column =
  | 'displayName'
  | 'daysWorked'
  | 'daysCharged'
  | 'minutes'
  | 'charged'
  | 'paidInPeriod'
  | 'outstanding'
  | 'rides'
  | 'takings';

interface Sort {
  column: Column;
  ascending: boolean;
}

export function ShiftBookFleet({
  report,
  onOpenDriver,
  onSettled,
}: {
  report: FleetShiftBook;
  onOpenDriver: (driverId: string) => void;
  onSettled: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<Sort>({ column: 'outstanding', ascending: false });
  const [settling, setSettling] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rows = useMemo(() => {
    const needle = filter.trim().toLocaleLowerCase();

    const visible = report.drivers.filter(
      (row) =>
        needle.length === 0 ||
        row.displayName.toLocaleLowerCase().includes(needle) ||
        (row.callSign ?? '').toLocaleLowerCase().includes(needle),
    );

    const direction = sort.ascending ? 1 : -1;

    return [...visible].sort((left, right) => direction * compare(left, right, sort.column));
  }, [report.drivers, filter, sort]);

  const totals = useMemo(() => sum(rows), [rows]);
  const fleet = useMemo(() => sum(report.drivers), [report.drivers]);

  const money = report.currency ?? formats.currency;
  const owing = report.drivers.filter((row) => row.outstanding > 0).length;

  async function settle(row: FleetShiftBookRow) {
    setSettling(row.driverId);
    setProblem(null);

    try {
      await reportsApi.recordPayment({
        driverId: row.driverId,
        // The amount currently on screen, over the period currently on screen. Marking a
        // driver paid is somebody in the office saying the cash arrived; it does not move
        // money, and it does not recompute anything.
        amount: row.outstanding,
        currency: row.currency ?? money,
        periodFrom: report.from,
        periodTo: report.to,
      });

      onSettled();
    } catch (error) {
      setProblem(describe(error, t, 'settings.couldNotSave'));
    } finally {
      setSettling(null);
    }
  }

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <Card>
          <StatTile
            label={t('reports.shiftBook.daysWorked')}
            value={formats.count(fleet.daysWorked)}
            // `count` picks the plural; `shown` is what is printed. i18next interpolates
            // with `String()`, so a figure handed to it raw would read "2700" beside the
            // same number formatted "2 700" in the total row eight lines below.
            sub={t('reports.shiftBook.daysChargedSub', {
              count: fleet.daysCharged,
              shown: formats.count(fleet.daysCharged),
            })}
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.hoursOnDuty')}
            value={formats.hours(fleet.minutes)}
            sub={t('reports.shiftBook.driverCount', {
              count: report.drivers.length,
              shown: formats.count(report.drivers.length),
            })}
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.charged', { currency: money })}
            value={formats.amount(fleet.charged)}
            sub={t('reports.shiftBook.paidSub', { amount: formats.amount(fleet.paid) })}
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.outstandingAllTimeIn', { currency: money })}
            value={formats.amount(fleet.outstanding)}
            // Warn only while something is owed. A nought in the colour of a warning is a
            // firm being told off for having settled up.
            tone={fleet.outstanding > 0 ? 'warn' : 'neutral'}
            sub={t('reports.shiftBook.driversOwe', { count: owing, shown: formats.count(owing) })}
          />
        </Card>
      </div>

      {report.rateMissing && (
        // Days worked is a fact; what they cost is a decision this firm has not made yet.
        <Banner tone="warn">{t('reports.shiftBook.noRate')}</Banner>
      )}

      <Card>
        <CardHeader
          actions={
            <span className="text-secondary text-ink-3">
              {t('reports.total', { count: rows.length })}
            </span>
          }
        >
          <div className="relative w-[260px]">
            <Icon
              name="search"
              small
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
            />
            <Input
              type="search"
              className="pl-8 font-normal"
              value={filter}
              aria-label={t('reports.shiftBook.filter')}
              placeholder={t('reports.shiftBook.filterPlaceholder')}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
        </CardHeader>

        {report.drivers.length === 0 ? (
          <EmptyState>{t('reports.shiftBook.nobodyWorked')}</EmptyState>
        ) : rows.length === 0 ? (
          // A total row of noughts under a search that found nobody reads as a fleet that
          // worked nothing, which is a different and much more alarming statement.
          <EmptyState>{t('reports.shiftBook.noMatch')}</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <Table tight>
              <thead>
                <tr>
                  <Sortable column="displayName" sort={sort} onSort={setSort}>
                    {t('reports.shiftBook.driver')}
                  </Sortable>
                  <Sortable column="daysWorked" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.daysWorked')}
                  </Sortable>
                  <Sortable column="daysCharged" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.daysCharged')}
                  </Sortable>
                  <Sortable column="minutes" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.hours')}
                  </Sortable>
                  <Sortable column="charged" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.charged', { currency: money })}
                  </Sortable>
                  <Sortable column="paidInPeriod" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.paidInPeriod', { currency: money })}
                  </Sortable>
                  <Sortable column="outstanding" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.outstanding', { currency: money })}
                  </Sortable>
                  <Sortable column="rides" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.rides')}
                  </Sortable>
                  <Sortable column="takings" sort={sort} onSort={setSort} numeric>
                    {t('reports.shiftBook.takings', { currency: money })}
                  </Sortable>
                  <Th scope="col">{t('reports.shiftBook.settled')}</Th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <Tr key={row.driverId} className={row.isActive ? undefined : 'text-ink-3'}>
                    <Td header>
                      <button
                        type="button"
                        className="font-semibold cursor-pointer hover:underline"
                        onClick={() => onOpenDriver(row.driverId)}
                      >
                        {row.displayName}
                      </button>
                      {row.callSign !== null && <Sub> · {row.callSign}</Sub>}
                      {!row.isActive && <Sub> · {t('reports.shiftBook.left')}</Sub>}
                    </Td>

                    <Td numeric>{formats.count(row.daysWorked)}</Td>

                    <Td numeric>
                      {formats.count(row.daysCharged)}
                      {row.unratedDays > 0 && (
                        <span title={t('reports.shiftBook.unratedDaysHint')}>
                          {' '}
                          <Sub>
                            {t('reports.shiftBook.unratedDays', { count: row.unratedDays })}
                          </Sub>
                        </span>
                      )}
                    </Td>

                    <Td numeric>{formats.hours(row.minutes)}</Td>
                    <Td numeric>{formats.amount(row.charged)}</Td>
                    <Td numeric>{formats.amount(row.paidInPeriod)}</Td>

                    <Td
                      numeric
                      className={row.outstanding > 0 ? 'text-warn font-semibold' : 'text-ink-3'}
                    >
                      {formats.amount(row.outstanding)}
                    </Td>

                    <Td numeric>
                      {formats.count(row.takings.rides)}
                      {row.takings.unpricedRides > 0 && (
                        <span title={t('reports.shiftBook.unpricedHint')}>
                          {' '}
                          <Sub>
                            {t('reports.shiftBook.unpriced', { count: row.takings.unpricedRides })}
                          </Sub>
                        </span>
                      )}
                    </Td>

                    <Td numeric>
                      {formats.amount(total(row))}
                      <Sub block>
                        {t('reports.shiftBook.cashCard', {
                          cash: formats.amount(row.takings.cash),
                          card: formats.amount(row.takings.card),
                        })}
                      </Sub>
                    </Td>

                    <Td>
                      {row.settled ? (
                        <span
                          className={cx(
                            'inline-flex items-center gap-1.5 text-secondary',
                            row.isActive ? 'text-ok' : '',
                          )}
                        >
                          <Icon name="check" small />
                          {t('reports.shiftBook.yes')}
                          {row.lastPaidAt !== null && <Sub>{formats.moment(row.lastPaidAt)}</Sub>}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          disabled={settling !== null || row.outstanding <= 0}
                          onClick={() => void settle(row)}
                        >
                          {settling === row.driverId
                            ? t('settings.saving')
                            : t('reports.shiftBook.markPaid')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}

                {/* The sum of what is displayed, so a filtered table adds up to itself. */}
                <Tr total>
                  <Td>{t('reports.total', { count: rows.length })}</Td>
                  <Td numeric>{formats.count(totals.daysWorked)}</Td>
                  <Td numeric>{formats.count(totals.daysCharged)}</Td>
                  <Td numeric>{formats.hours(totals.minutes)}</Td>
                  <Td numeric>{formats.amount(totals.charged)}</Td>
                  <Td numeric>{formats.amount(totals.paid)}</Td>
                  <Td numeric>{formats.amount(totals.outstanding)}</Td>
                  <Td numeric>{formats.count(totals.rides)}</Td>
                  <Td numeric>{formats.amount(totals.takings)}</Td>
                  <Td />
                </Tr>
              </tbody>
            </Table>
          </div>
        )}

        {problem !== null && (
          <Banner tone="danger" footer>
            {problem}
          </Banner>
        )}
      </Card>

      <div className="flex flex-col gap-0.5 text-secondary text-ink-3">
        <span>{t('reports.shiftBook.attribution')}</span>
        <span>{t('reports.shiftBook.outstandingIsAllTime')}</span>
      </div>
    </>
  );
}

/**
 * One sortable header.
 *
 * The arrow is an icon rather than a glyph, so it is the same 16px shape as every other
 * mark on the screen and inherits the header's colour.
 */
function Sortable({
  column,
  sort,
  onSort,
  numeric,
  children,
}: {
  column: Column;
  sort: Sort;
  onSort: (next: Sort) => void;
  numeric?: boolean;
  children: ReactNode;
}) {
  const current = sort.column === column;

  return (
    <Th
      numeric={numeric}
      scope="col"
      aria-sort={current ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 cursor-pointer hover:text-ink"
        // A first click on a new column sorts it the way somebody means it: names upward,
        // numbers downward. Anything else makes every column need two clicks.
        onClick={() =>
          onSort({ column, ascending: current ? !sort.ascending : column === 'displayName' })
        }
      >
        {children}
        {current && <Icon name={sort.ascending ? 'arrow-up' : 'arrow-down'} small />}
      </button>
    </Th>
  );
}

/**
 * Two columns are not a field on the row, and the rest are.
 *
 * The `default` arm subtracts, which is only correct because every remaining member of
 * `Column` is a number on `FleetShiftBookRow`. A new text column added to the union without
 * a `case` of its own compiles and then sorts by `NaN` — which looks like no sorting at all
 * rather than like a bug.
 */
function compare(left: FleetShiftBookRow, right: FleetShiftBookRow, column: Column): number {
  switch (column) {
    case 'displayName':
      return left.displayName.localeCompare(right.displayName);
    case 'rides':
      return left.takings.rides - right.takings.rides;
    case 'takings':
      return total(left) - total(right);
    default:
      return left[column] - right[column];
  }
}

function total(row: FleetShiftBookRow): number {
  return row.takings.cash + row.takings.card + row.takings.account + row.takings.unrecorded;
}

function sum(rows: FleetShiftBookRow[]) {
  return rows.reduce(
    (running, row) => ({
      daysWorked: running.daysWorked + row.daysWorked,
      daysCharged: running.daysCharged + row.daysCharged,
      minutes: running.minutes + row.minutes,
      charged: running.charged + row.charged,
      paid: running.paid + row.paidInPeriod,
      outstanding: running.outstanding + row.outstanding,
      rides: running.rides + row.takings.rides,
      takings: running.takings + total(row),
    }),
    {
      daysWorked: 0,
      daysCharged: 0,
      minutes: 0,
      charged: 0,
      paid: 0,
      outstanding: 0,
      rides: 0,
      takings: 0,
    },
  );
}
